import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Buildings,
  EnvelopeSimple,
  Phone,
  MapPin,
  PaperPlaneRight,
  Paperclip,
  ArrowRight,
  ArrowLeft,
  Info,
  X,
  CheckCircle,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import LegalConsent from '../components/LegalConsent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_FILES = 10;
const MAX_SIZE = 25 * 1024 * 1024;

// ---------- Catalogue ----------
const SERVICES = [
  { id: 'educational-consulting', label: 'الاستشارات التربوية وتطوير تجربة الطفل' },
  { id: 'administrative-consulting', label: 'الاستشارات الإدارية' },
  { id: 'quality-accreditation', label: 'الجودة والتطوير' },
  { id: 'kindergarten-establishment', label: 'تأسيس وتشغيل رياض الأطفال' },
  { id: 'policy-procedures', label: 'تطوير السياسات والإجراءات' },
  { id: 'professional-workshops', label: 'ورش العمل المهنية' },
  { id: 'accreditation', label: 'جاهزية الاعتماد' },
  { id: 'environment', label: 'جودة البيئة التعليمية' },
  { id: 'family-experience', label: 'التسجيل وتجربة الأسرة' },
  { id: 'classroom-design', label: 'تجهيزات البيئات الصفية' },
  { id: 'professional-dev', label: 'التطوير المهني للكادر' },
  { id: 'diagnostic', label: 'التقييم التشخيصي' },
  { id: 'workplace-nurseries', label: 'تأسيس وتشغيل روضات مقار العمل' },
  // Signature services
  { id: 'mystery-parent', label: 'ولي الأمر الخفي' },
  { id: 'preopening-readiness', label: 'مراجعة جاهزية الافتتاح' },
  { id: 'strategic-advisory', label: 'الشراكة الاستشارية' },
];

const ORG_TYPES = [
  'حضانة',
  'روضة أطفال',
  'مجمع طفولة مبكرة',
  'مركز تعليمي',
  'جهة أخرى',
];

const TARGET_AUDIENCES = [
  'حضانة',
  'رياض أطفال',
  'صفوف أولية',
  'الجميع (طفولة مبكرة)',
];

const CURRICULA = ['المنهج الوطني', 'منهج عالمي', 'أخرى'];

const PRIORITY = ['عاجل', 'أولوية متوسطة', 'مرن حسب الجدول'];

const CONTACT_PREFS = ['اتصال هاتفي', 'واتساب', 'بريد إلكتروني'];

// ---------- Service-specific questions ----------
// Each entry: { key, label, type, options? }
const SERVICE_QUESTIONS = {
  accreditation: {
    title: 'أسئلة مخصصة لخدمة جاهزية الاعتماد',
    items: [
      { key: 'A1', label: 'هل سبق للمنشأة التقديم على اعتماد أو تقييم؟', type: 'radio', options: ['نعم', 'لا', 'لا أعلم'] },
      { key: 'A2', label: 'هل توجد تقارير تقييم أو زيارات سابقة؟', type: 'radio', options: ['نعم', 'لا'] },
      {
        key: 'A3',
        label: 'ما المطلوب حالياً؟',
        type: 'checkbox',
        options: ['تشخيص الوضع الحالي', 'تحديد الفجوات', 'إعداد خطة تطوير', 'متابعة تنفيذ', 'تدريب الكوادر'],
      },
      { key: 'A4', label: 'ما أبرز التحديات الحالية؟', type: 'textarea' },
      { key: 'A5', label: 'هل يتطلب المشروع زيارة ميدانية؟', type: 'radio', options: ['نعم', 'لا', 'غير متأكد، أحتاج توصية'] },
    ],
  },
  environment: {
    title: 'أسئلة مخصصة لخدمة جودة البيئة التعليمية',
    items: [
      { key: 'B1', label: 'عدد الفصول أو البيئات المستهدفة بالتطوير', type: 'number' },
      {
        key: 'B2',
        label: 'ما نوع الدعم المطلوب؟',
        type: 'checkbox',
        options: [
          'تقييم البيئة التعليمية',
          'تحسين الأركان والتجهيزات',
          'تطوير الممارسات التدريسية',
          'تحسين السلامة والتنظيم',
          'إعداد توصيات تطويرية',
        ],
      },
      {
        key: 'B3',
        label: 'ما أبرز التحديات في البيئة الحالية؟',
        type: 'checkbox',
        options: [
          'ضعف التجهيزات',
          'عدم وضوح تنظيم الأركان',
          'محدودية المساحة',
          'ضعف توظيف المواد التعليمية',
          'الحاجة إلى رفع جودة الممارسات',
          'أخرى',
        ],
      },
      { key: 'B4', label: 'هل توجد صور أو مخططات للبيئة التعليمية؟', type: 'radio', options: ['نعم', 'لا'] },
      { key: 'B5', label: 'هل يتطلب المشروع زيارة ميدانية؟', type: 'radio', options: ['نعم', 'لا', 'غير متأكد، أحتاج توصية'] },
    ],
  },
  'family-experience': {
    title: 'أسئلة مخصصة لخدمة التسجيل وتجربة الأسرة',
    items: [
      {
        key: 'C1',
        label: 'كيف تتم عملية التسجيل حالياً؟',
        type: 'checkbox',
        options: ['حضوري', 'واتساب', 'إلكتروني', 'ورقي', 'مختلط'],
      },
      {
        key: 'C2',
        label: 'ما أبرز التحديات الحالية؟',
        type: 'checkbox',
        options: [
          'ضعف الاستقطاب',
          'بطء إجراءات التسجيل',
          'كثرة استفسارات أولياء الأمور',
          'انسحاب الأسر بعد التواصل',
          'عدم وضوح رحلة التسجيل',
          'ضعف رسائل التواصل',
        ],
      },
      { key: 'C3', label: 'هل يوجد فريق استقبال أو مسؤول تسجيل؟', type: 'radio', options: ['نعم', 'لا'] },
      { key: 'C4', label: 'هل توجد نماذج أو رسائل تواصل حالية مع الأسر؟', type: 'radio', options: ['نعم', 'لا'] },
      {
        key: 'C5',
        label: 'ما المطلوب تطويره؟',
        type: 'checkbox',
        options: [
          'رحلة التسجيل',
          'رسائل التواصل مع الأسر',
          'نماذج التسجيل',
          'تجربة ولي الأمر',
          'آلية المتابعة بعد الاستفسار',
        ],
      },
      { key: 'C6', label: 'هل يتطلب المشروع زيارة ميدانية؟', type: 'radio', options: ['نعم', 'لا', 'غير متأكد، أحتاج توصية'] },
    ],
  },
  'classroom-design': {
    title: 'أسئلة مخصصة لخدمة تجهيزات البيئات الصفية',
    items: [
      {
        key: 'D1',
        label: 'ما المرحلة المستهدفة؟',
        type: 'checkbox',
        options: ['حضانة', 'رياض أطفال', 'صفوف أولية', 'أكثر من مرحلة'],
      },
      {
        key: 'D2',
        label: 'ما نوع الدعم المطلوب؟',
        type: 'checkbox',
        options: [
          'تصميم بيئة صفية جديدة',
          'إعادة تنظيم بيئة قائمة',
          'اختيار الأثاث والتجهيزات',
          'مواءمة البيئة مع المنهج',
          'إعداد قائمة تجهيزات',
          'مراجعة السلامة والتنظيم',
        ],
      },
      {
        key: 'D3',
        label: 'ما المنهج أو التوجه المعتمد؟',
        type: 'checkbox',
        options: ['المنهج الوطني', 'مونتيسوري', 'ريجيو إميليا', 'هاي سكوب', 'منهج عالمي', 'غير محدد'],
      },
      { key: 'D4', label: 'هل توجد صور أو مخططات للمساحات؟', type: 'radio', options: ['نعم', 'لا'] },
      { key: 'D5', label: 'هل يتطلب المشروع زيارة ميدانية؟', type: 'radio', options: ['نعم', 'لا', 'غير متأكد، أحتاج توصية'] },
    ],
  },
  'professional-dev': {
    title: 'أسئلة مخصصة لخدمة التطوير المهني للكادر',
    items: [
      {
        key: 'E1',
        label: 'من الفئة المستهدفة بالتطوير؟',
        type: 'checkbox',
        options: ['معلمات', 'قائدات / مديرات', 'مشرفات', 'مساعدات معلمات', 'فريق استقبال', 'جميع الكادر'],
      },
      {
        key: 'E2',
        label: 'ما نوع الدعم المطلوب؟',
        type: 'checkbox',
        options: [
          'تدريب',
          'ورشة عمل',
          'ملاحظة صفية وتغذية راجعة',
          'بناء خطة تطوير مهني',
          'متابعة أثر التدريب',
        ],
      },
      {
        key: 'E3',
        label: 'ما أبرز الاحتياجات التدريبية؟',
        type: 'checkbox',
        options: [
          'جودة التفاعل مع الطفل',
          'إدارة البيئة الصفية',
          'التعلم باللعب',
          'التوثيق والتقييم',
          'التواصل مع الأسرة',
          'الاستعداد للاعتماد',
          'أخرى',
        ],
      },
      { key: 'E4', label: 'ما عدد المستفيدين المتوقع؟', type: 'number' },
      { key: 'E5', label: 'هل تفضلون التنفيذ؟', type: 'radio', options: ['حضوري', 'عن بعد', 'مزيج بينهما'] },
    ],
  },
  diagnostic: {
    title: 'أسئلة مخصصة لخدمة التقييم التشخيصي',
    items: [
      {
        key: 'F1',
        label: 'ما الهدف من التقييم التشخيصي؟',
        type: 'checkbox',
        options: [
          'معرفة الوضع الحالي',
          'تحديد الفجوات',
          'الاستعداد للاعتماد',
          'بناء خطة تحسين',
          'تقييم جودة البيئة والممارسات',
        ],
      },
      {
        key: 'F2',
        label: 'ما المجالات التي ترغبون في تقييمها؟',
        type: 'checkbox',
        options: [
          'القيادة والإدارة',
          'التعليم والتعلم',
          'البيئة التعليمية',
          'نواتج التعلم',
          'الرفاه والسلامة',
          'الشراكة مع الأسرة',
          'التقنية والبيانات',
        ],
      },
      { key: 'F3', label: 'هل سبق إجراء تقييم ذاتي داخلي؟', type: 'radio', options: ['نعم', 'لا', 'لا أعلم'] },
      { key: 'F4', label: 'هل توجد شواهد أو ملفات حالية؟', type: 'radio', options: ['نعم', 'لا'] },
      { key: 'F5', label: 'هل يتطلب المشروع زيارة ميدانية؟', type: 'radio', options: ['نعم', 'لا', 'غير متأكد، أحتاج توصية'] },
    ],
  },
  'kindergarten-establishment': {
    title: 'أسئلة مخصصة لتأسيس وتشغيل الحضانات ورياض الأطفال',
    items: [
      {
        key: 'K1',
        label: 'نوع المشروع',
        type: 'radio',
        options: ['حضانة', 'روضة أطفال', 'حضانة وروضة'],
      },
      {
        key: 'K2',
        label: 'حالة المشروع',
        type: 'radio',
        options: ['فكرة أولية', 'تحت التأسيس', 'قبل الافتتاح', 'منشأة قائمة'],
      },
      { key: 'K3', label: 'المدينة', type: 'text' },
      {
        key: 'K4',
        label: 'هل يوجد مقر؟',
        type: 'radio',
        options: ['نعم', 'لا'],
      },
      { key: 'K5', label: 'إجمالي عدد الفصول', type: 'number' },
      {
        key: 'K6',
        label: 'الطاقة الاستيعابية المتوقعة (إن عُرفت)',
        type: 'number',
      },
      {
        key: 'K7',
        label: 'هل يوجد منهج تعليمي؟',
        type: 'radio',
        options: ['نعم', 'لا'],
      },
      {
        key: 'K7a',
        label: 'نوع المنهج (يظهر عند اختيار «نعم» في السؤال السابق)',
        type: 'radio',
        options: ['المنهج الوطني', 'منهج عالمي', 'منهج مطور من الجهة', 'أخرى'],
      },
      {
        key: 'K8',
        label: 'هل توجد تراخيص؟',
        type: 'radio',
        options: ['نعم', 'لا', 'جارٍ استكمالها'],
      },
      { key: 'K9', label: 'الموعد المستهدف للافتتاح', type: 'text' },
      { key: 'K10', label: 'وصف الاحتياج أو التحدي', type: 'textarea' },
      {
        key: 'K11',
        label: 'الخدمات المطلوبة (اختيار متعدد)',
        type: 'checkbox',
        options: [
          'دراسة الجاهزية والافتتاح',
          'تصميم البيئة التعليمية',
          'النماذج التشغيلية والتنظيمية',
          'السياسات والحوكمة',
          'استقطاب وتأهيل الكوادر',
          'تجربة الأسرة والتسجيل',
          'التأهيل للجودة أو الاعتماد',
          'مرافقة التشغيل',
          'أحتاج إلى مساعدة في تحديد الاحتياج',
        ],
      },
    ],
  },
  'workplace-nurseries': {
    title: 'أسئلة مخصصة لخدمة تأسيس وتشغيل روضات مقار العمل',
    items: [
      {
        key: 'W1',
        label: 'طبيعة الجهة',
        type: 'radio',
        options: ['جهة حكومية', 'جهة شبه حكومية', 'قطاع خاص', 'قطاع غير ربحي', 'أخرى'],
      },
      {
        key: 'W2',
        label: 'حالة المشروع الحالية',
        type: 'radio',
        options: ['تحت التأسيس', 'قائم ويحتاج إلى تطوير', 'دراسة جدوى مبدئية', 'لم نبدأ بعد'],
      },
      {
        key: 'W3',
        label: 'ما نطاق الدعم المطلوب؟',
        type: 'checkbox',
        options: [
          'دراسة الجاهزية والاحتياج',
          'تصميم البيئة التعليمية',
          'تجهيزات الأركان والبيئات الصفية',
          'النماذج التشغيلية والتنظيمية',
          'سياسات التشغيل والحوكمة',
          'تجربة الأسرة والتسجيل',
          'تطوير الكوادر التعليمية والإدارية',
          'التأهيل للاعتماد والجودة',
          'مواءمة التشغيل مع بيئة العمل',
        ],
      },
      { key: 'W4', label: 'العدد التقديري للأطفال المستفيدين', type: 'number' },
      {
        key: 'W5',
        label: 'هل توجد مساحة مخصصة جاهزة داخل المقر؟',
        type: 'radio',
        options: ['نعم، جاهزة', 'نعم، تحتاج تجهيز', 'لا، نحتاج توصية'],
      },
      {
        key: 'W6',
        label: 'الإطار الزمني المتوقع للانطلاق',
        type: 'radio',
        options: ['خلال 3 أشهر', 'خلال 6 أشهر', 'خلال سنة', 'مرن'],
      },
      { key: 'W7', label: 'أبرز الأهداف من المشروع', type: 'textarea' },
      { key: 'W8', label: 'هل يتطلب المشروع زيارة ميدانية؟', type: 'radio', options: ['نعم', 'لا', 'غير متأكد، أحتاج توصية'] },
    ],
  },
};

// ---------- Building blocks ----------
const Field = ({ label, icon: Icon, required, children, help }) => (
  <div>
    <Label className="flex items-center gap-1.5 mb-1.5 text-[#1F3D2B]">
      {Icon && <Icon size={14} className="text-[#4A641F]" />}
      {label}
      {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {help && <p className="text-xs text-[#92A082] mt-1">{help}</p>}
  </div>
);

const RadioInline = ({ name, options, value, onChange, testid }) => (
  <RadioGroup
    value={value || ''}
    onValueChange={onChange}
    className="flex flex-wrap gap-3 mt-1"
    data-testid={testid}
  >
    {options.map((opt) => (
      <label
        key={opt}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors text-sm ${
          value === opt
            ? 'border-[#4A641F] bg-[#F5F2EC] text-[#1F3D2B]'
            : 'border-[#E6E0D6] hover:border-[#D8D2C8] text-[#3D512D]'
        }`}
      >
        <RadioGroupItem value={opt} id={`${name}-${opt}`} />
        <span>{opt}</span>
      </label>
    ))}
  </RadioGroup>
);

const CheckGroup = ({ options, values, onToggle, testid }) => (
  <div className="grid sm:grid-cols-2 gap-2 mt-1" data-testid={testid}>
    {options.map((opt) => {
      const checked = (values || []).includes(opt);
      return (
        <label
          key={opt}
          className={`flex items-start gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors text-sm ${
            checked
              ? 'border-[#4A641F] bg-[#F5F2EC] text-[#1F3D2B]'
              : 'border-[#E6E0D6] hover:border-[#D8D2C8] text-[#3D512D]'
          }`}
        >
          <Checkbox checked={checked} onCheckedChange={() => onToggle(opt)} className="mt-0.5" />
          <span>{opt}</span>
        </label>
      );
    })}
  </div>
);

// ---------- Page ----------
const QuotationRequestPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialService = params.get('service') || '';

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [stage1, setStage1] = useState({
    organization: '',
    contact_name: '',
    phone: '',
    email: '',
    city: '',
    org_type: '',
    target_audience: [],
    org_status: '',
    children_count: '',
    rooms_count: '',
    space_size: '',
    staff_count: '',
    curriculum: '',
    service: initialService,
    description: '',
    has_files: '',
    priority: '',
    contact_pref: '',
  });
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    if (initialService) setStage1((s) => ({ ...s, service: initialService }));
  }, [initialService]);

  const update = (k, v) => setStage1((s) => ({ ...s, [k]: v }));
  const toggleArray = (k, value) => {
    setStage1((s) => {
      const arr = s[k] || [];
      return {
        ...s,
        [k]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });
  };

  const toggleAnswerArray = (k, value) => {
    setAnswers((a) => {
      const arr = a[k] || [];
      return {
        ...a,
        [k]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });
  };

  const addFiles = (list) => {
    const arr = Array.from(list);
    const next = [...files];
    for (const f of arr) {
      if (next.length >= MAX_FILES) {
        toast.error(`الحد الأقصى ${MAX_FILES} ملفات`);
        break;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name}: يتجاوز ٢٥ ميجابايت`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const validateStage1 = () => {
    const required = {
      organization: 'اسم الجهة / المنشأة',
      contact_name: 'اسم المسؤول عن التواصل',
      phone: 'رقم الجوال',
      email: 'البريد الإلكتروني',
      city: 'المدينة / المنطقة',
      org_type: 'نوع المنشأة',
      org_status: 'حالة المنشأة',
      service: 'الخدمة المطلوبة',
      description: 'وصف مختصر للاحتياج',
      contact_pref: 'طريقة التواصل المفضلة',
    };
    for (const [k, label] of Object.entries(required)) {
      if (!stage1[k]) {
        toast.error(`يرجى تعبئة: ${label}`);
        return false;
      }
    }
    if ((stage1.target_audience || []).length === 0) {
      toast.error('يرجى اختيار الفئة المستهدفة');
      return false;
    }
    return true;
  };

  const onNext = () => {
    if (!validateStage1()) return;
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentSpec = SERVICE_QUESTIONS[stage1.service];
  const serviceLabel = useMemo(
    () => SERVICES.find((s) => s.id === stage1.service)?.label || '',
    [stage1.service]
  );

  const buildNotes = () => {
    const lines = [
      `[طلب عرض سعر - ${serviceLabel}]`,
      '',
      '— البيانات الأساسية —',
      `الجهة/المنشأة: ${stage1.organization}`,
      `المسؤول: ${stage1.contact_name}`,
      `الجوال: ${stage1.phone}`,
      `البريد: ${stage1.email}`,
      `المدينة/المنطقة: ${stage1.city}`,
      `نوع المنشأة: ${stage1.org_type}`,
      `الفئة المستهدفة: ${(stage1.target_audience || []).join('، ')}`,
      `حالة المنشأة: ${stage1.org_status}`,
      stage1.children_count && `عدد الأطفال التقريبي: ${stage1.children_count}`,
      stage1.rooms_count && `عدد الفصول/القاعات: ${stage1.rooms_count}`,
      stage1.space_size && `المساحة التقريبية: ${stage1.space_size}`,
      stage1.staff_count && `عدد الكوادر: ${stage1.staff_count}`,
      stage1.curriculum && `المنهج المطبق: ${stage1.curriculum}`,
      `الخدمة المطلوبة: ${serviceLabel}`,
      '',
      '— الوصف —',
      stage1.description,
      '',
      stage1.has_files && `يوجد ملفات حالية: ${stage1.has_files}`,
      stage1.priority && `أولوية المشروع: ${stage1.priority}`,
      `طريقة التواصل المفضلة: ${stage1.contact_pref}`,
    ].filter(Boolean);

    if (currentSpec) {
      lines.push('', `— ${currentSpec.title} —`);
      currentSpec.items.forEach((q) => {
        const v = answers[q.key];
        if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return;
        const val = Array.isArray(v) ? v.join('، ') : v;
        lines.push(`${q.label}: ${val}`);
      });
    }
    return lines.join('\n');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setConsentError('يجب الموافقة على سياسة الخصوصية والشروط والأحكام قبل الإرسال');
      toast.error('يرجى الموافقة على السياسات قبل الإرسال');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', stage1.contact_name);
      fd.append('organization', stage1.organization);
      fd.append('email', stage1.email);
      fd.append('phone', stage1.phone);
      fd.append('service_type', 'diagnostic_review');
      fd.append('notes', buildNotes());
      files.forEach((f) => fd.append('files', f));

      const res = await fetch(`${API_URL}/api/service-requests`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'فشل الإرسال');
      }
      const data = await res.json();
      navigate(`/quotation/success?token=${data.access_token}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="quotation-page"
      className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]"
      dir="rtl"
    >
      <section className="py-10 bg-[#F5F2EC]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            عرض سعر مخصص
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
            طلب عرض سعر مخصص
          </h1>
          <p className="text-[#6B7565] leading-8 text-sm sm:text-base">
            يرجى تعبئة البيانات الأساسية واختيار نوع الخدمة المطلوبة، وستظهر أسئلة
            مخصصة بحسب الخدمة المختارة. تساعدنا هذه البيانات على فهم الاحتياج
            وتحديد نطاق العمل، ثم التواصل معكم لإعداد عرض سعر مناسب.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-8">
            <StepBadge n={1} label="البيانات الأساسية" active={step >= 1} done={step > 1} />
            <div className="w-10 sm:w-16 h-px bg-[#D8D2C8]" />
            <StepBadge n={2} label="أسئلة مخصصة للخدمة" active={step >= 2} />
          </div>

          <motion.form
            onSubmit={step === 2 ? submit : (e) => { e.preventDefault(); onNext(); }}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[24px] border border-[#E6E0D6] p-5 sm:p-8 shadow-sm space-y-6"
          >
            {step === 1 ? (
              <>
                <div
                  data-testid="quotation-intro"
                  className="bg-[#F5F2EC] border border-[#E6E0D6] rounded-2xl px-5 py-4 sm:px-6 sm:py-5"
                >
                  <p className="text-sm sm:text-[15px] text-[#3D512D] leading-8 sm:leading-9">
                    نساعدك في فهم احتياج منشأتك بشكل أدق قبل إعداد عرض السعر.
                    <br className="hidden sm:block" />
                    سيتم توجيهك عبر مجموعة أسئلة قصيرة ومخصصة بحسب نوع الخدمة،
                    بهدف تحديد نطاق العمل المناسب وتقديم توصية أكثر دقة وواقعية.
                  </p>
                </div>

                <h2 className="text-lg sm:text-xl font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic']">
                  البيانات الأساسية
                </h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="اسم الجهة / المنشأة" icon={Buildings} required>
                    <Input data-testid="q-org" value={stage1.organization} onChange={(e) => update('organization', e.target.value)} className="rounded-xl" />
                  </Field>
                  <Field label="اسم المسؤول عن التواصل" required>
                    <Input data-testid="q-name" value={stage1.contact_name} onChange={(e) => update('contact_name', e.target.value)} className="rounded-xl" />
                  </Field>
                  <Field label="رقم الجوال" icon={Phone} required>
                    <Input data-testid="q-phone" value={stage1.phone} onChange={(e) => update('phone', e.target.value)} dir="ltr" placeholder="05xxxxxxxx" className="rounded-xl text-right" />
                  </Field>
                  <Field label="البريد الإلكتروني" icon={EnvelopeSimple} required>
                    <Input data-testid="q-email" type="email" value={stage1.email} onChange={(e) => update('email', e.target.value)} dir="ltr" className="rounded-xl text-right" />
                  </Field>
                  <Field label="المدينة / المنطقة" icon={MapPin} required>
                    <Input data-testid="q-city" value={stage1.city} onChange={(e) => update('city', e.target.value)} className="rounded-xl" />
                  </Field>
                  <Field label="نوع المنشأة" required>
                    <select data-testid="q-orgtype" value={stage1.org_type} onChange={(e) => update('org_type', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#D8D2C8] bg-white text-[#1F3D2B] text-sm">
                      <option value="">اختاري...</option>
                      {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="الفئة المستهدفة" required>
                  <CheckGroup options={TARGET_AUDIENCES} values={stage1.target_audience} onToggle={(v) => toggleArray('target_audience', v)} testid="q-audience" />
                </Field>

                <Field label="حالة المنشأة" required>
                  <RadioInline name="org_status" options={['قائمة', 'تحت التأسيس']} value={stage1.org_status} onChange={(v) => update('org_status', v)} testid="q-status" />
                </Field>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="عدد الأطفال التقريبي">
                    <Input data-testid="q-children" type="number" min="0" value={stage1.children_count} onChange={(e) => update('children_count', e.target.value)} className="rounded-xl" />
                  </Field>
                  <Field label="عدد الفصول أو القاعات">
                    <Input data-testid="q-rooms" type="number" min="0" value={stage1.rooms_count} onChange={(e) => update('rooms_count', e.target.value)} className="rounded-xl" />
                  </Field>
                  <Field label="مساحة المنشأة التقريبية">
                    <Input data-testid="q-space" value={stage1.space_size} onChange={(e) => update('space_size', e.target.value)} placeholder="مثال: 500 متر مربع" className="rounded-xl" />
                  </Field>
                  <Field label="عدد الكوادر التعليمية والإدارية">
                    <Input data-testid="q-staff" type="number" min="0" value={stage1.staff_count} onChange={(e) => update('staff_count', e.target.value)} className="rounded-xl" />
                  </Field>
                </div>

                <Field label="المنهج أو البرنامج المطبق">
                  <RadioInline name="curriculum" options={CURRICULA} value={stage1.curriculum} onChange={(v) => update('curriculum', v)} testid="q-curriculum" />
                </Field>

                <Field label="الخدمة المطلوبة" required>
                  <select data-testid="q-service" value={stage1.service} onChange={(e) => update('service', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#D8D2C8] bg-white text-[#1F3D2B] text-sm">
                    <option value="">اختاري الخدمة...</option>
                    {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>

                <Field label="وصف مختصر للاحتياج" required>
                  <Textarea data-testid="q-desc" rows={4} value={stage1.description} onChange={(e) => update('description', e.target.value)} className="rounded-xl" placeholder="اكتب باختصار ما الذي تحتاجه المنشأة أو ما التحدي الحالي." />
                </Field>

                <Field label="هل توجد ملفات أو نماذج حالية يمكن مشاركتها؟">
                  <RadioInline name="has_files" options={['نعم', 'لا']} value={stage1.has_files} onChange={(v) => update('has_files', v)} testid="q-hasfiles" />
                </Field>

                <Field label="رفع الملفات" help={`الصيغ المدعومة: PDF, DOCX, JPG, PNG — حد أقصى ${MAX_FILES} ملفات / ٢٥ ميجابايت لكل ملف`}>
                  <label htmlFor="qfiles" className="block border-2 border-dashed border-[#D8D2C8] rounded-2xl p-5 text-center cursor-pointer hover:border-[#4A641F] transition-colors">
                    <Paperclip size={22} className="mx-auto text-[#7A866E] mb-1" />
                    <p className="text-sm text-[#1F3D2B]">اختاري الملفات</p>
                    <input id="qfiles" type="file" multiple accept={ALLOWED} data-testid="q-files-input" className="sr-only" onChange={(e) => addFiles(e.target.files)} />
                  </label>
                  {files.length > 0 && (
                    <ul className="space-y-1 mt-3">
                      {files.map((f, idx) => (
                        <li key={idx} className="text-sm text-[#1F3D2B] flex items-center justify-between bg-[#F5F2EC] rounded-lg px-3 py-2">
                          <span className="flex items-center gap-2 min-w-0">
                            <Paperclip size={14} className="text-[#4A641F]" />
                            <span className="truncate">{f.name}</span>
                          </span>
                          <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))}>
                            <X size={16} className="text-[#7A866E]" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>

                <Field label="درجة أولوية المشروع">
                  <RadioInline name="priority" options={PRIORITY} value={stage1.priority} onChange={(v) => update('priority', v)} testid="q-priority" />
                </Field>

                <Field label="طريقة التواصل المفضلة" required>
                  <RadioInline name="contact_pref" options={CONTACT_PREFS} value={stage1.contact_pref} onChange={(v) => update('contact_pref', v)} testid="q-pref" />
                </Field>

                <Button
                  type="submit"
                  data-testid="q-next-btn"
                  className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-4 text-base font-bold flex items-center justify-center gap-2"
                >
                  المتابعة إلى الأسئلة المخصصة
                  <ArrowLeft size={18} className="rtl-flip" />
                </Button>
              </>
            ) : (
              <>
                <div className="bg-[#F5F2EC] rounded-xl p-4 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-[#3D512D]">
                    الخدمة المختارة: <strong className="text-[#1F3D2B]">{serviceLabel}</strong>
                  </span>
                  <button
                    type="button"
                    data-testid="q-edit-btn"
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-1 text-[#4A641F] hover:underline text-xs"
                  >
                    <ArrowRight size={14} className="rtl-flip" />
                    رجوع وتعديل البيانات
                  </button>
                </div>

                {currentSpec ? (
                  <>
                    <h2 className="text-lg sm:text-xl font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic']">
                      {currentSpec.title}
                    </h2>
                    <div className="space-y-5">
                      {currentSpec.items.map((q) => (
                        <Field key={q.key} label={q.label}>
                          {q.type === 'radio' && (
                            <RadioInline
                              name={q.key}
                              options={q.options}
                              value={answers[q.key]}
                              onChange={(v) => setAnswers({ ...answers, [q.key]: v })}
                              testid={`q-${q.key}`}
                            />
                          )}
                          {q.type === 'checkbox' && (
                            <CheckGroup
                              options={q.options}
                              values={answers[q.key] || []}
                              onToggle={(v) => toggleAnswerArray(q.key, v)}
                              testid={`q-${q.key}`}
                            />
                          )}
                          {q.type === 'textarea' && (
                            <Textarea
                              data-testid={`q-${q.key}`}
                              rows={3}
                              value={answers[q.key] || ''}
                              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                              className="rounded-xl"
                            />
                          )}
                          {q.type === 'number' && (
                            <Input
                              data-testid={`q-${q.key}`}
                              type="number"
                              min="0"
                              value={answers[q.key] || ''}
                              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                              className="rounded-xl"
                            />
                          )}
                        </Field>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-center text-[#7A866E] py-6">
                    لا توجد أسئلة مخصصة لهذه الخدمة — يمكنك إرسال الطلب مباشرة.
                  </p>
                )}

                <div className="space-y-2 text-xs text-[#7A866E] leading-7 bg-[#F5F2EC] rounded-xl p-4">
                  <p className="flex items-start gap-2">
                    <Info size={14} className="text-[#4A641F] mt-0.5 shrink-0" />
                    يتم تحديد نطاق العمل النهائي والتكلفة بعد مراجعة البيانات والملفات المرفقة.
                  </p>
                  <p className="flex items-start gap-2">
                    <Info size={14} className="text-[#4A641F] mt-0.5 shrink-0" />
                    قد يتم تحديد الحاجة للزيارة الميدانية بعد مراجعة البيانات.
                  </p>
                </div>

                <LegalConsent
                  checked={consent}
                  onChange={(v) => { setConsent(v); if (v) setConsentError(''); }}
                  error={consentError}
                  testId="q-legal-consent"
                />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="sm:flex-1 border-[#D8D2C8] text-[#1F3D2B] rounded-xl py-4 flex items-center justify-center gap-2"
                  >
                    <ArrowRight size={18} className="rtl-flip" />
                    رجوع
                  </Button>
                  <Button
                    type="submit"
                    data-testid="q-submit-btn"
                    disabled={submitting}
                    className="sm:flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-4 text-base font-bold flex items-center justify-center gap-2"
                  >
                    <PaperPlaneRight size={18} />
                    {submitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                  </Button>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </section>
    </main>
  );
};

const StepBadge = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
        active ? 'bg-[#1F3D2B] text-white' : 'bg-[#E6E0D6] text-[#7A866E]'
      }`}
    >
      {done ? <CheckCircle size={16} weight="fill" /> : n}
    </div>
    <span className={`text-xs sm:text-sm font-medium ${active ? 'text-[#1F3D2B]' : 'text-[#7A866E]'}`}>
      {label}
    </span>
  </div>
);

export default QuotationRequestPage;
